import { audioContext } from './audio';
import { ENGINE_VOICES, engineAt, type EngineVoice } from './engineModel';

/** Kept low: the engine sits under the radio and the horn. */
const ENGINE_VOLUME = 0.020;
const ROAD_VOLUME = 0.010;
/** Seconds for the sound to glide to its new pitch (smooths gear changes and frame-to-frame jitter). */
const GLIDE = 0.14;
/** How much the cylinder firing pulses the volume: the "putt-putt" of a diesel. */
const THROB_DEPTH = 0.35;

function noiseBuffer(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let brown = 0;
  for (let i = 0; i < data.length; i += 1) {
    brown = (brown + 0.02 * (Math.random() * 2 - 1)) / 1.02;
    data[i] = brown * 3.5;
  }
  return buffer;
}

interface Nodes {
  ctx: AudioContext;
  master: GainNode;
  low: OscillatorNode;
  sub: OscillatorNode;
  throb: OscillatorNode;
  filter: BiquadFilterNode;
  engineGain: GainNode;
  roadFilter: BiquadFilterNode;
  roadGain: GainNode;
  road: AudioBufferSourceNode;
}

/**
 * The trotro's engine and tyre/road noise, following the speed. A soft sawtooth plus a sub-octave through a
 * gentle lowpass gives the body; a slow pulse on the volume gives the diesel's firing throb; the road is
 * low, rumbly noise rather than hiss.
 */
export class EngineSound {
  private nodes: Nodes | null = null;
  private voice: EngineVoice = ENGINE_VOICES.sprinter!;
  private lastSpeed = 0;

  setVehicle(id: string): void {
    this.voice = ENGINE_VOICES[id] ?? ENGINE_VOICES.sprinter!;
  }

  private build(): Nodes | null {
    const ctx = audioContext();
    if (!ctx) return null;
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.7;
    // Cuts the deep rumble phone speakers can't play anyway (it only muddies the mix).
    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 45;
    const engineGain = ctx.createGain();
    // Firing throb: an LFO nudging a gain stage up and down around 1.
    const pulse = ctx.createGain();
    pulse.gain.value = 1;
    const throb = ctx.createOscillator();
    throb.type = 'sine';
    const throbDepth = ctx.createGain();
    throbDepth.gain.value = THROB_DEPTH;
    throb.connect(throbDepth).connect(pulse.gain);
    filter.connect(highpass).connect(pulse).connect(engineGain).connect(master);
    const low = ctx.createOscillator();
    low.type = 'sawtooth';
    const sub = ctx.createOscillator();
    sub.type = 'triangle';
    const subGain = ctx.createGain();
    subGain.gain.value = 0.6;
    const lowGain = ctx.createGain();
    lowGain.gain.value = 0.5;
    low.connect(lowGain).connect(filter);
    sub.connect(subGain).connect(filter);
    const road = ctx.createBufferSource();
    road.buffer = noiseBuffer(ctx);
    road.loop = true;
    const roadFilter = ctx.createBiquadFilter();
    roadFilter.type = 'lowpass';
    roadFilter.Q.value = 0.5;
    const roadGain = ctx.createGain();
    roadGain.gain.value = 0;
    road.connect(roadFilter).connect(roadGain).connect(master);
    low.start();
    sub.start();
    throb.start();
    road.start();
    return { ctx, master, low, sub, throb, filter, engineGain, roadFilter, roadGain, road };
  }

  /** Fade in (call when a shift starts or resumes). */
  start(): void {
    this.nodes ??= this.build();
    const n = this.nodes;
    if (!n) return;
    n.master.gain.setTargetAtTime(1, n.ctx.currentTime, 0.3);
  }

  /** Fade out (pause, menu, end of shift). The nodes stay ready for the next shift. */
  stop(): void {
    const n = this.nodes;
    if (n) n.master.gain.setTargetAtTime(0, n.ctx.currentTime, 0.15);
  }

  /** Call every frame while driving. */
  update(speed: number, dt: number): void {
    const n = this.nodes;
    if (!n || dt <= 0) return;
    const accelerating = speed > this.lastSpeed + 0.02;
    this.lastSpeed = speed;
    const e = engineAt(speed, accelerating, this.voice);
    const t = n.ctx.currentTime;
    n.low.frequency.setTargetAtTime(e.hz * 2, t, GLIDE);
    n.sub.frequency.setTargetAtTime(e.hz, t, GLIDE);
    // Firing pulses: fast enough to feel like an engine, slow enough to hear as a throb.
    n.throb.frequency.setTargetAtTime(Math.min(28, e.hz / 3), t, GLIDE);
    const [dull, bright] = this.voice.brightness;
    n.filter.frequency.setTargetAtTime(dull + (bright - dull) * e.load, t, GLIDE);
    n.engineGain.gain.setTargetAtTime(ENGINE_VOLUME * (0.55 + 0.45 * e.load), t, GLIDE);
    const roadShare = Math.min(1, speed / 40);
    n.roadGain.gain.setTargetAtTime(ROAD_VOLUME * roadShare * roadShare, t, 0.3);
    n.roadFilter.frequency.setTargetAtTime(180 + 420 * roadShare, t, 0.3);
  }
}
